import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import { useClientTheme } from '../../../../../helpers/hooks/useClientTheme';
import { AlertAnnotation } from '../../../../../model/passive/alertMoment';
import { IProcessedTemperatureData } from '../../../../../model/passive/temperatureData';

const ChartContainer = styled(Box)({
  height: '100%',
  width: '100%',
  minHeight: '200px',
  position: 'relative',
});

const NoDataContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

interface RightFootChartProps {
  data: IProcessedTemperatureData[];
  height?: number | string;
  selectedDateRange?: [Date | null, Date | null];
  chartRef?: React.MutableRefObject<any>;
  onZoomChange?: (chart: any) => void;
  alertAnnotations?: AlertAnnotation[];
  onAlertAnnotationClick?: (alertId: number) => void;
}

const RightFootChart: React.FC<RightFootChartProps> = ({
  data,
  height = '100%',
  selectedDateRange,
  chartRef: externalChartRef,
  onZoomChange,
  alertAnnotations = [],
  onAlertAnnotationClick,
}) => {
  const { theme } = useClientTheme();
  const { t } = useTranslation('dailyParameters');
  const secondaryColor = theme?.palette?.secondary?.main || '#dc004e';
  const internalChartRef = useRef<any>(null);
  const chartRef = externalChartRef || internalChartRef;

  // Calculate the date range in days
  const calculateRangeDays = (): number => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return 1;
    const diffMs = selectedDateRange[1].getTime() - selectedDateRange[0].getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const rangeDays = calculateRangeDays();

  // Configurable thresholds for time unit detection
  const SINGLE_DAY_THRESHOLD = 1;
  const WEEK_THRESHOLD = 7;

  // Determine time unit and display format based on range
  const getTimeConfig = () => {
    if (rangeDays <= SINGLE_DAY_THRESHOLD) {
      // Single day - show hours
      return {
        unit: 'hour' as const,
        displayFormats: {
          hour: 'HH:mm',
          day: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy HH:mm',
      };
    } else if (rangeDays <= WEEK_THRESHOLD) {
      // Up to a week - show hours but with day boundaries emphasized
      return {
        unit: 'hour' as const,
        displayFormats: {
          hour: 'HH:mm',
          day: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy HH:mm',
        stepSize: 6, // Show every 6 hours to avoid crowding
      };
    } else {
      // More than a week - show days only
      return {
        unit: 'day' as const,
        displayFormats: {
          day: 'dd MMM',
          week: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy',
      };
    }
  };

  const timeConfig = getTimeConfig();

  // Fixed y-axis: 20 degree range centred on the average right foot temperature
  const getYAxisBounds = () => {
    const validData = data.filter((item) => item.averageRightTemperature > 0);
    if (validData.length === 0) return {};
    const avg = validData.reduce((sum, item) => sum + item.averageRightTemperature, 0) / validData.length;
    return { min: avg - 10, max: avg + 10 };
  };

  const yAxisBounds = getYAxisBounds();

  // Calculate min/max to include full days
  const getAxisBounds = () => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return {};

    // Set min to start of first day (00:00:00)
    const minDate = new Date(selectedDateRange[0]);
    minDate.setHours(0, 0, 0, 0);

    // Set max to end of last day (23:59:59)
    const maxDate = new Date(selectedDateRange[1]);
    maxDate.setHours(23, 59, 59, 999);

    return {
      min: minDate.getTime(),
      max: maxDate.getTime(),
    };
  };

  const axisBounds = getAxisBounds();

  // Format data for the chart - 3 lines for Top, Mid, Rear sensors
  const formatChartData = (): ChartData<'line'> => {
    // Map data to x,y coordinates with Date objects for time scale
    const topData = data.map((item) => ({
      x: new Date(item.date).getTime(),
      y: item.rightTemperatures.length > 0 ? item.rightTemperatures[0] : null,
    }));

    const midData = data.map((item) => ({
      x: new Date(item.date).getTime(),
      y: item.rightTemperatures.length > 1 ? item.rightTemperatures[1] : null,
    }));

    const rearData = data.map((item) => ({
      x: new Date(item.date).getTime(),
      y: item.rightTemperatures.length > 2 ? item.rightTemperatures[2] : null,
    }));

    return {
      datasets: [
        {
          label: t('charts.series.toes'),
          data: topData,
          borderColor: '#2196f3', // Blue
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 3,
          spanGaps: 19 * 60 * 1000, // Only span gaps less than 19 minutes (in milliseconds)
        },
        {
          label: t('charts.series.metatarsalHeads'),
          data: midData,
          borderColor: '#ff9800', // Orange
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 3,
          spanGaps: 19 * 60 * 1000, // Only span gaps less than 19 minutes (in milliseconds)
        },
        {
          label: t('charts.series.heel'),
          data: rearData,
          borderColor: '#4caf50', // Green
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 3,
          spanGaps: 19 * 60 * 1000, // Only span gaps less than 19 minutes (in milliseconds)
        },
      ],
    };
  };

  const buildAnnotations = () => {
    const annotations: Record<string, any> = {};
    alertAnnotations.forEach((ann, i) => {
      annotations[`alert_box_${i}`] = {
        type: 'box',
        xMin: ann.timestamp - THIRTY_MINUTES_MS,
        xMax: ann.timestamp + THIRTY_MINUTES_MS,
        backgroundColor: ann.color + '26',
        borderWidth: 0,
        click: ann.alertId !== undefined && onAlertAnnotationClick
          ? () => onAlertAnnotationClick(ann.alertId!)
          : undefined,
        enter: ann.alertId !== undefined && onAlertAnnotationClick
          ? ({ chart }: any) => { chart.canvas.style.cursor = 'pointer'; }
          : undefined,
        leave: ann.alertId !== undefined && onAlertAnnotationClick
          ? ({ chart }: any) => { chart.canvas.style.cursor = 'default'; }
          : undefined,
      };
      annotations[`alert_line_${i}`] = {
        type: 'line',
        xMin: ann.timestamp,
        xMax: ann.timestamp,
        borderColor: ann.color,
        borderWidth: 2,
      };
    });
    return annotations;
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ...yAxisBounds,
        title: {
          display: true,
          text: t('charts.axis.temperature'),
        },
      },
      x: {
        type: 'timeseries',
        time: {
          unit: timeConfig.unit,
          displayFormats: timeConfig.displayFormats,
          tooltipFormat: timeConfig.tooltipFormat,
        },
        ...axisBounds,
        title: {
          display: true,
          text: t('charts.axis.dateTime'),
        },
        ticks: {
          source: 'auto',
          maxRotation: 0,
          major: {
            enabled: true,
          },
          font: function(context) {
            if (context.tick && context.tick.major) {
              return {
                weight: 'bold',
              };
            }
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return value !== null ? `${context.dataset.label}: ${value}°C` : '';
          },
          title: (context) => {
            const date = new Date(context[0].parsed.x ?? 0);
            return date.toLocaleString();
          },
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: false,
          },
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: false,
          },
          mode: 'xy',
          onZoomComplete: ({ chart }: any) => {
            if (onZoomChange) {
              onZoomChange(chart);
            }
          },
        },
        pan: {
          enabled: true,
          mode: 'xy',
          modifierKey: 'shift',
          onPanComplete: ({ chart }: any) => {
            if (onZoomChange) {
              onZoomChange(chart);
            }
          },
        },
        limits: {
          x: { min: 'original', max: 'original' },
        },
      },
      annotation: {
        annotations: buildAnnotations(),
      },
    },
  };

  if (data.length === 0) {
    return (
      <NoDataContainer sx={{ height }}>
        <Typography variant="body2" color="textSecondary">
          {t('charts.noData')}
        </Typography>
      </NoDataContainer>
    );
  }

  return (
    <ChartContainer sx={{ height }}>
      <Line ref={chartRef} data={formatChartData()} options={chartOptions as any} />
    </ChartContainer>
  );
};

export default RightFootChart;

